import {
  type ParticipantDocumentScoringResult,
  type ScoringChecklist,
  ScoringConfidence,
  type ScoringCriteriaResult,
  ScoringResult,
  type TemplateScoringCriteria,
} from '@piccolohealth/pbs-common';
import { ChakraV3, ScrollArea } from '@piccolohealth/ui';
import { P } from '@piccolohealth/util';
import React from 'react';
import { FaRegCheckCircle, FaRegTimesCircle } from 'react-icons/fa';
import { createModal } from '../../components/generic/Modal';
import { PiccoloError } from '../../components/generic/PiccoloError';
import { useAppContext } from '../../hooks/useAppContext';
import { useScoreParticipantDocumentJob } from '../../hooks/useJob';
import {
  type ScoringResultsFilter,
  useScoringResultsFilter,
} from '../../hooks/useScoringResultsFilter';
import { ScoringResultsFilterControls } from './ScoringResultsFilterControls';

interface ScoreSummaryProps {
  results: ScoringCriteriaResult[];
}

const ScoreSummary = (props: ScoreSummaryProps) => {
  const { results } = props;

  const passed = results.filter((r) => r.result === ScoringResult.Passed).length;
  const failed = results.filter((r) => r.result === ScoringResult.Failed).length;
  const total = results.length;

  return (
    <ChakraV3.Stack w='full' gap={3} lineHeight={1.2}>
      <ChakraV3.Stack
        p={3}
        rounded='lg'
        color='green.500'
        bg='green.50'
        align='center'
        w='full'
        gap={0}
        justify='center'
      >
        <ChakraV3.Text fontSize='3xl' fontWeight='bold'>
          {passed}
        </ChakraV3.Text>
        <ChakraV3.Text fontSize='sm'>Passed</ChakraV3.Text>
      </ChakraV3.Stack>
      <ChakraV3.Stack
        p={3}
        rounded='lg'
        color='red.500'
        bg='red.50'
        align='center'
        w='full'
        gap={0}
        justify='center'
      >
        <ChakraV3.Text fontSize='3xl' fontWeight='bold'>
          {failed}
        </ChakraV3.Text>
        <ChakraV3.Text fontSize='sm'>Failed</ChakraV3.Text>
      </ChakraV3.Stack>
      <ChakraV3.Stack
        p={3}
        rounded='lg'
        color='gray.500'
        bg='gray.50'
        align='center'
        w='full'
        gap={0}
        justify='center'
      >
        <ChakraV3.Text fontSize='3xl' fontWeight='bold'>
          {total}
        </ChakraV3.Text>
        <ChakraV3.Text fontSize='sm'>Total</ChakraV3.Text>
      </ChakraV3.Stack>
    </ChakraV3.Stack>
  );
};

type CriteriaResultProps = {
  checklist: ScoringChecklist;
  criteriaIndex: number;
  categoryIndex: number;
  criteria: TemplateScoringCriteria;
  result: ScoringCriteriaResult;
};

const CriteriaResult = (props: CriteriaResultProps) => {
  const { checklist, criteriaIndex, categoryIndex, criteria, result } = props;

  const { resultColor, resultIcon } =
    result.result === ScoringResult.Passed
      ? { resultColor: 'green.500', resultIcon: FaRegCheckCircle }
      : { resultColor: 'red.500', resultIcon: FaRegTimesCircle };

  const { confidenceColor } = P.run(() => {
    switch (result.confidence) {
      case ScoringConfidence.High:
        return { confidenceColor: 'green' };
      case ScoringConfidence.Medium:
        return { confidenceColor: 'yellow' };
      case ScoringConfidence.Low:
        return { confidenceColor: 'red' };
      default:
        return { confidenceColor: 'gray' };
    }
  });

  const labels = React.useMemo(() => {
    return checklist.criteriaLabels.filter((label) => criteria.labelIds.includes(label.id));
  }, [checklist, criteria]);

  return (
    <ChakraV3.Stack layerStyle='bordered' p={6}>
      <ChakraV3.HStack gap={8} align='start'>
        <ChakraV3.Icon as={resultIcon} color={resultColor} fontSize='3xl' />

        <ChakraV3.Stack gap={2}>
          <ChakraV3.Text fontSize='md' fontWeight='semibold'>
            {categoryIndex + 1}.{criteriaIndex + 1} - {criteria.description}
          </ChakraV3.Text>
          <ChakraV3.Text fontSize='sm' color='gray.500'>
            {result.details}
          </ChakraV3.Text>
          <ChakraV3.HStack w='full' rowGap={1}>
            {labels.map((label) => (
              <ChakraV3.Tag key={label.id} colorPalette={label.color} size='sm'>
                {label.name}
              </ChakraV3.Tag>
            ))}
          </ChakraV3.HStack>
        </ChakraV3.Stack>
        <ChakraV3.Spacer />

        <ChakraV3.Tag size='sm' colorPalette={confidenceColor} flexShrink={0}>
          {result.confidence} confidence
        </ChakraV3.Tag>
      </ChakraV3.HStack>
    </ChakraV3.Stack>
  );
};

interface CategoryResultsProps {
  results: ScoringCriteriaResult[];
  checklist: ScoringChecklist;
  filter: ScoringResultsFilter;
}

const CategoryResults = (props: CategoryResultsProps) => {
  const { results, checklist, filter } = props;

  const filteredResults = React.useMemo(() => {
    return results.filter((result) => {
      const criteria = checklist.categories
        .flatMap((category) => category.criterias)
        .find((c) => c.id === result.id);

      if (!criteria) {
        return false;
      }

      if (filter.searchFilter) {
        const searchTerm = filter.searchFilter.toLowerCase();
        const matchesDescription = criteria.description.toLowerCase().includes(searchTerm);
        const matchesDetails = result.details.toLowerCase().includes(searchTerm);
        if (!matchesDescription && !matchesDetails) {
          return false;
        }
      }

      if (!P.isEmpty(filter.confidenceFilter)) {
        if (!filter.confidenceFilter.includes(result.confidence)) {
          return false;
        }
      }

      if (!P.isEmpty(filter.resultFilter)) {
        if (!filter.resultFilter.includes(result.result)) {
          return false;
        }
      }

      if (!P.isEmpty(filter.labelFilter)) {
        const hasMatchingLabel = criteria.labelIds.some((labelId) =>
          filter.labelFilter.includes(labelId),
        );
        if (!hasMatchingLabel) {
          return false;
        }
      }

      return true;
    });
  }, [results, checklist, filter]);

  if (P.isEmpty(filteredResults)) {
    return (
      <ChakraV3.Text fontSize='sm' color='secondary'>
        No results found
      </ChakraV3.Text>
    );
  }

  return checklist.categories.map((category, categoryIndex) => {
    const categoryResults = P.compact(
      category.criterias.map((criteria, criteriaIndex) => {
        const result = filteredResults.find((r) => r.id === criteria.id);

        return result
          ? {
              result,
              criteria,
              criteriaIndex,
              categoryIndex,
            }
          : null;
      }),
    );

    if (P.isEmpty(categoryResults)) {
      return null;
    }

    return (
      <ChakraV3.Stack key={category.id}>
        <ChakraV3.Text fontSize='lg' fontWeight='semibold'>
          {category.description}
        </ChakraV3.Text>

        <ChakraV3.Stack gap={4}>
          {categoryResults.map((item) => (
            <CriteriaResult
              key={item!.criteria.id}
              checklist={checklist}
              criteriaIndex={item!.criteriaIndex}
              categoryIndex={item!.categoryIndex}
              criteria={item!.criteria}
              result={item!.result}
            />
          ))}
        </ChakraV3.Stack>
      </ChakraV3.Stack>
    );
  });
};

interface ScoringResultSidebarProps {
  results: ScoringCriteriaResult[];
  checklist: ScoringChecklist | null;
}

const ScoringResultSidebar = (props: ScoringResultSidebarProps) => {
  const { results, checklist } = props;

  const labelCounts = React.useMemo(() => {
    if (!checklist) {
      return [];
    }

    const criteria = checklist.categories.flatMap((category) => category.criterias);
    const criteriaLabels = checklist.criteriaLabels || [];

    return criteriaLabels
      .map((label) => {
        const labelCriteria = criteria.filter((c) => c.labelIds.includes(label.id));
        const labelResults = results.filter((r) => labelCriteria.some((c) => c.id === r.id));

        const total = labelCriteria.length;
        const passing = labelResults.filter((r) => r.result === ScoringResult.Passed).length;

        return {
          name: label.name,
          color: label.color,
          total,
          passing,
        };
      })
      .filter((labelCount) => labelCount.total > 0);
  }, [checklist, results]);

  const content = P.run(() => {
    if (!checklist || P.isEmpty(results)) {
      return (
        <ChakraV3.Stack gap={2}>
          <ChakraV3.Text fontSize='sm' color='secondary'>
            No scoring results available
          </ChakraV3.Text>
        </ChakraV3.Stack>
      );
    }

    return (
      <ChakraV3.Stack gap={4}>
        <ScoreSummary results={results} />

        <ChakraV3.Stack>
          <ChakraV3.Text fontSize='lg' fontWeight='semibold'>
            Labels Summary
          </ChakraV3.Text>
          <ChakraV3.Stack>
            {P.isEmpty(labelCounts) && (
              <ChakraV3.Text fontSize='sm' color='secondary'>
                No labels used
              </ChakraV3.Text>
            )}
            {labelCounts.map((l) => (
              <ChakraV3.HStack key={l.name} justify='space-between' align='center'>
                <ChakraV3.Tag size='sm' colorPalette={l.color}>
                  <ChakraV3.TagLabel>{l.name}</ChakraV3.TagLabel>
                </ChakraV3.Tag>
                <ChakraV3.Text fontSize='sm' color='secondary'>
                  {l.passing}/{l.total}
                </ChakraV3.Text>
              </ChakraV3.HStack>
            ))}
          </ChakraV3.Stack>
        </ChakraV3.Stack>
      </ChakraV3.Stack>
    );
  });

  return (
    <ChakraV3.Stack h='full' gap={0}>
      <ChakraV3.Text fontSize='lg' fontWeight='semibold' mb={4}>
        Summary
      </ChakraV3.Text>

      {content}
    </ChakraV3.Stack>
  );
};

interface Props {
  participantId: string;
  participantDocumentId: string;
  scoringResult: ParticipantDocumentScoringResult | null;
}

export const ParticipantDocumentScoringResultModal = createModal<Props>((props) => {
  const { participantId, participantDocumentId, scoringResult, modal } = props;
  const { hide, remove, visible } = modal;

  const { organization } = useAppContext();

  const scoringJob = useScoreParticipantDocumentJob();
  const filter = useScoringResultsFilter();

  const onClick = async () => {
    await scoringJob.start({
      organizationId: organization.id,
      participantId: participantId,
      participantDocumentId: participantDocumentId,
    });
  };

  const jobResult = scoringJob.isCompleted
    ? (scoringJob.job?.response as ParticipantDocumentScoringResult)
    : null;

  // If the scoring result already exists from DB, use it instead of the job
  const activeResult = scoringResult ?? jobResult;

  const checklist = activeResult?.checklist as ScoringChecklist;
  const results: ScoringCriteriaResult[] = activeResult?.results ?? [];

  const content = P.run(() => {
    if (!activeResult && !scoringJob.error) {
      return (
        <ChakraV3.Center h='full'>
          <ChakraV3.Alert.Root
            status='info'
            variant='subtle'
            flexDirection='column'
            alignItems='center'
            justifyContent='center'
            textAlign='center'
            w='fit-content'
            p={12}
          >
            <ChakraV3.Alert.Indicator boxSize='40px' mr={0} />
            <ChakraV3.Alert.Content>
              <ChakraV3.Alert.Title mt={4} mb={1} fontSize='lg'>
                Important disclaimer
              </ChakraV3.Alert.Title>
              <ChakraV3.Alert.Description mt={5}>
                <ChakraV3.Stack textAlign='left' gap={1}>
                  <ChakraV3.Text>
                    • AI reviews are suggestions only and may contain errors
                  </ChakraV3.Text>
                  <ChakraV3.Text>
                    • Always verify AI recommendations with human judgment
                  </ChakraV3.Text>
                  <ChakraV3.Text>
                    • Sensitive or confidential content will be processed by AI
                  </ChakraV3.Text>
                  <ChakraV3.Text>
                    • Results may vary and are not guaranteed to be accurate
                  </ChakraV3.Text>
                </ChakraV3.Stack>
              </ChakraV3.Alert.Description>
              <ChakraV3.Button
                colorPalette='brand'
                onClick={onClick}
                loading={scoringJob.isLoading}
                mt={8}
                loadingText='Analyzing document, this can take a few minutes...'
              >
                Analyze document
              </ChakraV3.Button>
            </ChakraV3.Alert.Content>
          </ChakraV3.Alert.Root>
        </ChakraV3.Center>
      );
    }

    if (scoringJob.error) {
      return <PiccoloError error={scoringJob.error} />;
    }

    return (
      <ChakraV3.HStack w='full' h='full' gap={0}>
        <ChakraV3.Stack w='full' h='full' gap={4}>
          <ScoringResultsFilterControls filter={filter} checklist={checklist} />
          <ChakraV3.Stack h='full' overflow='hidden'>
            <ScrollArea h='full'>
              <ChakraV3.Stack gap={6} pr={4}>
                <CategoryResults results={results} checklist={checklist} filter={filter} />
              </ChakraV3.Stack>
            </ScrollArea>
          </ChakraV3.Stack>
        </ChakraV3.Stack>
        <ChakraV3.Stack
          h='full'
          w='320px'
          gap={4}
          flexShrink={0}
          borderLeftWidth='1px'
          borderLeftColor='gray.200'
          p={4}
        >
          <ScoringResultSidebar results={results} checklist={checklist} />
        </ChakraV3.Stack>
      </ChakraV3.HStack>
    );
  });

  return (
    <ChakraV3.Dialog.Root
      open={visible}
      onOpenChange={hide}
      onExitComplete={remove}
      size='cover'
      scrollBehavior='inside'
    >
      <ChakraV3.Dialog.Backdrop />
      <ChakraV3.Dialog.Positioner>
        <ChakraV3.Dialog.Content>
          <ChakraV3.DialogTitleSubtitle
            title='Review this document with AI'
            subtitle='Please review the results and make any necessary adjustments'
          />
          <ChakraV3.Dialog.Body>{content}</ChakraV3.Dialog.Body>
          <ChakraV3.Dialog.Footer>
            <ChakraV3.Button key='close' colorPalette='gray' variant='subtle' onClick={hide}>
              Close
            </ChakraV3.Button>
          </ChakraV3.Dialog.Footer>
        </ChakraV3.Dialog.Content>
      </ChakraV3.Dialog.Positioner>
    </ChakraV3.Dialog.Root>
  );
});
